import React from 'react';
import { Helmet } from 'react-helmet';

interface StructuredDataProps {
  type?: 'website' | 'mobileapp' | 'howto';
  path?: string;
}

const StructuredData: React.FC<StructuredDataProps> = ({
  type = 'website',
  path = ''
}) => {
  const baseUrl = 'https://pasurscorekeeper.com';

  // Website schema with mobile app focus
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Pasur Score Keeper',
    url: baseUrl,
    description: 'The classic Iranian card game Pasur, reimagined for mobile. Play with friends in real-time or asynchronously. Download free on iOS and Android.',
  };

  // Mobile Application schema
  const mobileAppSchema = {
    '@context': 'https://schema.org',
    '@type': 'MobileApplication',
    name: 'Pasur Score Keeper',
    operatingSystem: 'iOS, Android',
    applicationCategory: 'GameApplication',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD'
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      ratingCount: '50'
    },
    downloadUrl: [
      'https://apps.apple.com/us/app/pasur-online-play-score/id6476008595',
      'https://play.google.com/store/apps/details?id=com.vakilventures.pasur'
    ],
    description: 'Track scores for the classic Iranian card game Pasur. Play multiplayer with friends in real-time or asynchronously. Features offline play, push notifications, and automatic score calculation.',
    screenshot: `${baseUrl}/images/app-screenshot.png`,
    featureList: 'Multiplayer modes, Offline play, Push notifications, Automatic scoring'
  };

  // HowTo schema for rules page
  const howToSchema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'How to Play Pasur Card Game',
    description: 'Learn how to play the traditional Iranian card game Pasur with these step-by-step instructions.',
    step: [
      {
        '@type': 'HowToStep',
        name: 'Setup',
        text: 'Deal 4 cards to each player and place 4 cards face up on the table.'
      },
      {
        '@type': 'HowToStep',
        name: 'Play Cards',
        text: 'On your turn, play one card from your hand. Cards A-10 capture table cards that sum to 11.'
      },
      {
        '@type': 'HowToStep',
        name: 'Special Cards',
        text: 'Jacks sweep all cards except Kings/Queens. Kings and Queens only capture same rank.'
      },
      {
        '@type': 'HowToStep',
        name: 'Scoring',
        text: 'Score points for Sur (5 pts), Most Clubs (7 pts), 10 of Diamonds (3 pts), 2 of Clubs (2 pts), and each Ace/Jack (1 pt). First to 62 wins!'
      }
    ]
  };

  let schemaData;

  switch (type) {
    case 'mobileapp':
      schemaData = mobileAppSchema;
      break;
    case 'howto':
      schemaData = howToSchema;
      break;
    default:
      schemaData = websiteSchema;
  }

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schemaData)}
      </script>
    </Helmet>
  );
};

export default StructuredData;
